import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterGroupingEnum, User } from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import UserFieldNames from '@lib/enums/fieldNames/userFieldNames';
import DepartmentsModalTableMultiselect from 'components/DepartmentsModalTableMultiselect/DepartmentsModalTableMultiselect';
import getDefaultValuesFromQueryData from 'routes/Users/utils/getDefaultValuesFromQueryData';
import useActions from './utils/useActions';
import UserForm from './UserForm';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSubmitCallback: (v?: User | null) => void;
  selectedUserId?: string | null;
}

function UserFormContainer(props: Props) {
  const { isOpen, onCancel, onSubmitCallback, selectedUserId } = props;
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const [isDepartmentsModalOpen, setDepartmentsModalOpen] = useState(false);

  const {
    countriesCodes,
    countriesError,
    getDepartmentsLoadOptions,
    graphQLErrors,
    linkedDepartments,
    loading,
    onSubmitUserForm,
    sectorsLoadOptions,
    selectedUser,
    sitesLoadOptions,
    teamsLoadOptions,
    userError,
    userLoading,
  } = useActions({
    selectedUserId,
    isOpen,
    onSubmitCallback,
  });

  const openDepartmentsModalTableMultiselect = useCallback(() => {
    setDepartmentsModalOpen(true);
  }, []);

  const closeDepartmentsModalTableMultiselect = useCallback(() => {
    setDepartmentsModalOpen(false);
  }, []);

  const onDepartmentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        UserFieldNames.DepartmentMemberships,
        values.length
          ? values.map((departmentId) => ({ value: departmentId }))
          : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  if (userLoading) {
    return <CircleLoader />;
  }

  if (userError) {
    return (
      <ErrorPage
        status={getStatusFromError(userError)}
        description={getDescriptionFromError(userError)}
      />
    );
  }

  if (countriesError) {
    return (
      <ErrorPage
        status={getStatusFromError(countriesError)}
        description={getDescriptionFromError(countriesError)}
      />
    );
  }

  const defaultValues = getDefaultValuesFromQueryData(
    t,
    {
      ...selectedUser,
    },
    linkedDepartments,
  );

  return (
    <>
      <UserForm
        countriesCodes={countriesCodes}
        defaultValues={defaultValues}
        formRef={formRef}
        getDepartmentsLoadOptions={getDepartmentsLoadOptions}
        graphQLErrors={graphQLErrors}
        loading={loading}
        onCancel={onCancel}
        onSubmit={onSubmitUserForm}
        openDepartmentsModalTableMultiselect={
          openDepartmentsModalTableMultiselect
        }
        sectorsLoadOptions={sectorsLoadOptions}
        sitesLoadOptions={sitesLoadOptions}
        teamsLoadOptions={teamsLoadOptions}
      />
      <DepartmentsModalTableMultiselect
        isOpen={isDepartmentsModalOpen}
        onClose={closeDepartmentsModalTableMultiselect}
        onSelect={onDepartmentsSelect}
        formRef={formRef}
        fieldName={UserFieldNames.DepartmentMemberships}
        selectedTabFilters={
          selectedUserId
            ? [
                {
                  usersId: {
                    grouping: FilterGroupingEnum.And,
                    predicate: { eq: selectedUserId },
                  },
                },
              ]
            : undefined
        }
      />
    </>
  );
}

export default UserFormContainer;
